import React, { useEffect, useState } from "react";
import { FormField } from "@avamae/formbuilder";
import { FormFieldTooltip } from "components/helpers/FormFieldTooltip/FormFieldTooltip";

import { ErrorM } from "components/helpers/FormHelpers/ErrorM";
import { Input } from "components/helpers/FormHelpers/Input";
import { TooltipIconData } from "@avamae/formbuilder/dist/FormBuilder";
import { useField, useFormikContext } from "formik";
import { after } from "lodash";

export interface FormTextFieldProps<T> {
  fieldName: string;
  label: string;
  readOnly?: boolean;
  afterLabelText?: string;
  placeholderText?: string;
  inputProps?: JSX.IntrinsicElements["input"];
  tooltip?: string | null;
  tooltipIconData?: TooltipIconData | null;
  isNumber?: boolean;
  wrapText?: boolean;
}

const FormTextField = function<T>({
  fieldName,
  label,
  readOnly,
  placeholderText,
  afterLabelText,
  inputProps,
  tooltipIconData,
  tooltip,
  isNumber,
  wrapText,
}: FormTextFieldProps<T>) {
  const [field, meta] = useField(fieldName as string);
  const isPlaceholder = placeholderText && placeholderText !== "";
  const [textAreaValue, setTextAreaValue] = useState<string>("");

  useEffect(() => {
    if (field.value) {
      setTextAreaValue(field.value);
    }
  }, []);

  const { setFieldValue } = useFormikContext<any>();

  const onTextChange = (e: any) => {
    setFieldValue(field.name, e.target.value);
  };
  if (isNumber) {
    inputProps = { ...inputProps, type: "number" };
  }
  return (
    <div
      className={`FormBox FormSideBySide StringReadOnlyEditFieldRenderer ${
        wrapText ? "WrapText" : ""
      }`}
    >
      <div className="FormLabel">
        <label>
          {label}
          {afterLabelText && (
            <span>
              <i className="pseudo">{afterLabelText}</i>
            </span>
          )}
        </label>

        {tooltip &&
          tooltipIconData &&
          tooltipIconData.mainIcon &&
          tooltipIconData.closeIcon && (
            <FormFieldTooltip
              tooltipText={tooltip}
              tooltipMainIconSource={tooltipIconData.mainIcon}
              tooltipCloseIconSource={tooltipIconData.closeIcon}
            />
          )}
      </div>
      <div className="FormField">
        {isPlaceholder && (
          <div className="EditBox placeholderTextWrapper">
            <div className="placeText">{placeholderText}</div>
            <input
              className="PlaceholderInputBox"
              type={isNumber ? "number" : "text"}
              onChange={onTextChange}
              value={
                readOnly && inputProps?.value ? inputProps?.value : field.value
              }
            />
          </div>
        )}
        {!isPlaceholder && (
          <>
            {!wrapText ? (
              <Input
                name={fieldName.toString()}
                className={`${readOnly ? "EditBox ViewOnly" : "EditBox"} ${
                  isPlaceholder ? "hide" : ""
                }`}
                readOnly={readOnly}
                {...inputProps}
                type="string"
                value={
                  readOnly && inputProps?.value
                    ? inputProps?.value
                    : field.value
                }
              />
            ) : (
              <div className="TextArea">
                <div className="Text">
                  {readOnly && inputProps?.value
                    ? inputProps?.value
                    : textAreaValue}
                </div>
              </div>
            )}
          </>
        )}
        <ErrorM name={fieldName.toString()} />
      </div>
    </div>
  );
};

export { FormTextField };
type PlaceholderTextFields = {
  [fieldName: string]: string;
};
export type labelAfterContentFields = {
  [fieldName: string]: string;
};

export const stringReadOnlyEditRenderer = (
  placeholderTextFields: PlaceholderTextFields | null,
  labelAfterContentFields: labelAfterContentFields | null,
  isNumber: boolean = false,
  wrapText?: boolean
) =>
  function stringV2Renderer<T>(_key: React.ReactText, field: FormField<T>) {
    return (
      <FormTextField
        label={field.label as string}
        placeholderText={
          placeholderTextFields &&
          placeholderTextFields.hasOwnProperty(field.name)
            ? placeholderTextFields[
                field.name as keyof typeof placeholderTextFields
              ]
            : undefined
        }
        fieldName={String(field.name)}
        afterLabelText={
          labelAfterContentFields &&
          labelAfterContentFields.hasOwnProperty(field.name)
            ? labelAfterContentFields[
                field.name as keyof typeof labelAfterContentFields
              ]
            : undefined
        }
        isNumber={isNumber}
        tooltip={field.toolTip}
        wrapText={wrapText}
      />
    );
  };
